type PersistedUser = {
  id: string;
  email: string;
  username: string;
  role: 'STUDENT' | 'TEACHER' | 'ADMIN';
  avatar?: string;
};

type PersistedAuthState = {
  user: PersistedUser | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
};

type AuthScope = 'student' | 'teacher' | 'unknown';

const LEGACY_USER_KEY = 'user';
const LEGACY_TOKEN_KEY = 'token';
const QUICK_LOGIN_NAME_KEY = 'quick-login-display-name';

const STORAGE_KEYS = {
  student: {
    user: 'auth:student:user',
    token: 'auth:student:token',
    refreshToken: 'auth:student:refresh-token',
  },
  teacher: {
    user: 'auth:teacher:user',
    token: 'auth:teacher:token',
    refreshToken: 'auth:teacher:refresh-token',
  },
} as const;

const isQuickLoginEmail = (email?: string | null) => Boolean(email?.startsWith('quick_'));

const parseUser = (rawUser: string | null): PersistedUser | null => {
  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser) as PersistedUser;
  } catch {
    return null;
  }
};

const emptyAuthState = (): PersistedAuthState => ({
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
});

const getAuthScopeFromPath = (pathname = typeof window !== 'undefined' ? window.location.pathname : ''): AuthScope => {
  if (pathname.startsWith('/student')) {
    return 'student';
  }

  if (pathname.startsWith('/teacher')) {
    return 'teacher';
  }

  return 'unknown';
};

const getScopeFromRole = (role: PersistedUser['role']): Exclude<AuthScope, 'unknown'> =>
  role === 'STUDENT' ? 'student' : 'teacher';

const getStorageKeys = (scope: Exclude<AuthScope, 'unknown'>) => STORAGE_KEYS[scope];

const removeScopeAuth = (scope: Exclude<AuthScope, 'unknown'>) => {
  const keys = getStorageKeys(scope);
  localStorage.removeItem(keys.user);
  localStorage.removeItem(keys.token);
  localStorage.removeItem(keys.refreshToken);
  sessionStorage.removeItem(keys.user);
  sessionStorage.removeItem(keys.token);
  sessionStorage.removeItem(keys.refreshToken);
};

const loadScopedAuth = (scope: Exclude<AuthScope, 'unknown'>): PersistedAuthState => {
  const keys = getStorageKeys(scope);
  const sessionUser = parseUser(sessionStorage.getItem(keys.user));
  const sessionToken = sessionStorage.getItem(keys.token);
  const sessionRefreshToken = sessionStorage.getItem(keys.refreshToken);

  if (sessionUser && sessionToken) {
    return {
      user: sessionUser,
      token: sessionToken,
      refreshToken: sessionRefreshToken,
      isAuthenticated: true,
    };
  }

  const localUser = parseUser(localStorage.getItem(keys.user));
  const localToken = localStorage.getItem(keys.token);
  const localRefreshToken = localStorage.getItem(keys.refreshToken);

  if (localUser && localToken) {
    if (isQuickLoginEmail(localUser.email)) {
      localStorage.removeItem(keys.user);
      localStorage.removeItem(keys.token);
      localStorage.removeItem(keys.refreshToken);
      return emptyAuthState();
    }

    return {
      user: localUser,
      token: localToken,
      refreshToken: localRefreshToken,
      isAuthenticated: true,
    };
  }

  return emptyAuthState();
};

const loadLegacyAuth = (): PersistedAuthState => {
  const sessionUser = parseUser(sessionStorage.getItem(LEGACY_USER_KEY));
  const sessionToken = sessionStorage.getItem(LEGACY_TOKEN_KEY);

  if (sessionUser && sessionToken) {
    return {
      user: sessionUser,
      token: sessionToken,
      refreshToken: null,
      isAuthenticated: true,
    };
  }

  const localUser = parseUser(localStorage.getItem(LEGACY_USER_KEY));
  const localToken = localStorage.getItem(LEGACY_TOKEN_KEY);

  if (!localUser || !localToken) {
    return emptyAuthState();
  }

  if (isQuickLoginEmail(localUser.email)) {
    localStorage.removeItem(LEGACY_USER_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    return emptyAuthState();
  }

  return {
    user: localUser,
    token: localToken,
    refreshToken: null,
    isAuthenticated: true,
  };
};

const migrateLegacyAuthIfNeeded = (scope: Exclude<AuthScope, 'unknown'>) => {
  const scoped = loadScopedAuth(scope);
  if (scoped.isAuthenticated) {
    return scoped;
  }

  const legacy = loadLegacyAuth();
  if (!legacy.isAuthenticated || !legacy.user) {
    return emptyAuthState();
  }

  if (getScopeFromRole(legacy.user.role) !== scope) {
    return emptyAuthState();
  }

  persistAuth(legacy.user, legacy.token || '');
  localStorage.removeItem(LEGACY_USER_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  sessionStorage.removeItem(LEGACY_USER_KEY);
  sessionStorage.removeItem(LEGACY_TOKEN_KEY);

  return loadScopedAuth(scope);
};

export const clearPersistedAuth = () => {
  const scope = getAuthScopeFromPath();

  if (scope === 'student' || scope === 'teacher') {
    removeScopeAuth(scope);
  } else {
    removeScopeAuth('student');
    removeScopeAuth('teacher');
  }

  localStorage.removeItem(LEGACY_USER_KEY);
  localStorage.removeItem(LEGACY_TOKEN_KEY);
  sessionStorage.removeItem(LEGACY_USER_KEY);
  sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  sessionStorage.removeItem(QUICK_LOGIN_NAME_KEY);
};

export const loadPersistedAuth = (): PersistedAuthState => {
  const scope = getAuthScopeFromPath();

  if (scope === 'student' || scope === 'teacher') {
    return migrateLegacyAuthIfNeeded(scope);
  }

  const studentAuth = migrateLegacyAuthIfNeeded('student');
  if (studentAuth.isAuthenticated) {
    return studentAuth;
  }

  const teacherAuth = migrateLegacyAuthIfNeeded('teacher');
  if (teacherAuth.isAuthenticated) {
    return teacherAuth;
  }

  return emptyAuthState();
};

export const persistAuth = (user: PersistedUser, token: string, refreshToken: string | null = null) => {
  const scope = getScopeFromRole(user.role);
  const keys = getStorageKeys(scope);

  if (isQuickLoginEmail(user.email)) {
    localStorage.removeItem(keys.user);
    localStorage.removeItem(keys.token);
    localStorage.removeItem(keys.refreshToken);
    sessionStorage.setItem(keys.user, JSON.stringify(user));
    sessionStorage.setItem(keys.token, token);
    if (refreshToken) {
      sessionStorage.setItem(keys.refreshToken, refreshToken);
    } else {
      sessionStorage.removeItem(keys.refreshToken);
    }
    return;
  }

  sessionStorage.removeItem(keys.user);
  sessionStorage.removeItem(keys.token);
  sessionStorage.removeItem(keys.refreshToken);
  localStorage.setItem(keys.user, JSON.stringify(user));
  localStorage.setItem(keys.token, token);
  if (refreshToken) {
    localStorage.setItem(keys.refreshToken, refreshToken);
  } else {
    localStorage.removeItem(keys.refreshToken);
  }
};
